import React from 'react';
import { useTranslation } from 'react-i18next';

import { CButton } from '@app/components/Common';
import { Text, View } from '@/styles/reactnative';
import { useToken } from '@app/hooks';
import { COLORS } from '@app/constants';
import { TRANSFER } from '@app/constants/APIKEYS';
import API from '@utils/api';
import { toast } from 'react-toastify';
import { useBalance } from '../../../hooks/user/mybalance';
import { useTradingWallets } from '../../../hooks/user/useTradingWallets';
import {
  transferToTradingWallet,
  transferFromTradingWallet,
} from '../../../api/wallet';
import { USER_LOG_TYPE, useUserLogger } from '@/app/hooks/userLogger';
import { message } from 'antd';

interface TransferFooterProps {
  transferFrom: number | 'BOT' | 'COPY';
  transferTo: number | 'BOT' | 'COPY';
  amount: string | number;
  coinType: string;
  balance: number;
}

const TransferFooter: React.FC<TransferFooterProps> = ({
  transferFrom,
  transferTo,
  amount,
  coinType,
  balance,
}) => {
  const { t } = useTranslation();
  const { data: token } = useToken();

  // Legacy 지갑 타입인 경우에만 useBalance 호출
  const fromType = typeof transferFrom === 'number' ? transferFrom : 0;
  const toType = typeof transferTo === 'number' ? transferTo : 0;

  const { mutate: mutateFrom } = useBalance(coinType, fromType);
  const { mutate: mutateTo } = useBalance(coinType, toType);
  const { mutate: mutateTradingWallets } = useTradingWallets();
  const { mutateUserLog } = useUserLogger();

  const [isTrasferring, setIsTransferring] = React.useState(false);

  const type = 'USDT';

  const logConvert: Record<number | 'BOT' | 'COPY', string> = {
    0: '현물지갑',
    1: '선물지갑',
    9: '전체지갑',
    7: '리워드지갑',
    BOT: '봇지갑',
    COPY: '카피트레이딩지갑',
  };

  const onTransfer = async () => {
    if (isTrasferring) {
      return message.error(t('transferInProgress'));
    }

    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (numAmount <= 0 || isNaN(numAmount)) {
      return message.error(t('invalidAmount') || '유효하지 않은 금액입니다');
    }

    if (numAmount > balance) {
      return message.error(t('insufficientBalance') || '잔액이 부족합니다');
    }

    setIsTransferring(true); // 전송 시작

    // 봇/카피 지갑 관련 이체인지 확인
    const isNewWallet =
      transferFrom === 'BOT' ||
      transferFrom === 'COPY' ||
      transferTo === 'BOT' ||
      transferTo === 'COPY';

    const log = `${logConvert[transferFrom]} -> ${logConvert[transferTo]} (${amount}테더)`;

    try {
      if (isNewWallet) {
        // ===== 새 API 사용 (봇/카피 지갑) =====

        // 서버 API 지원 조건 검증:
        // - /transfer: SPOT → BOT/COPY만 지원 (FUTURES → BOT/COPY 미지원)
        // - /transfer/reverse: BOT/COPY → SPOT/FUTURES 지원
        if (transferFrom === 1 && (transferTo === 'BOT' || transferTo === 'COPY')) {
          // 선물 → 봇/카피 전송은 서버에서 미지원
          return message.error(
            t('futuresTransferNotSupported') ||
            '선물지갑에서 봇/카피지갑으로 직접 전송은 지원되지 않습니다. 현물지갑을 경유해 주세요.'
          );
        }

        mutateUserLog({
          log: `[지갑이동 요청 - 신규API]: ${log}`,
          data: { transferFrom, transferTo, amount },
        });

        let response;

        if (transferFrom === 0) {
          // 현물 → 봇/카피 (FUTURES는 위에서 차단됨)
          response = await transferToTradingWallet({
            amount: numAmount,
            fromType: 'SPOT',
            toType: transferTo as 'BOT' | 'COPY',
          });
        } else {
          // 봇/카피 → 현물/선물
          response = await transferFromTradingWallet({
            amount: numAmount,
            fromType: transferFrom as 'BOT' | 'COPY',
            toType: transferTo === 0 ? 'SPOT' : 'FUTURES',
          });
        }

        if (response.success) {
          mutateUserLog({
            log: `[지갑이동 성공 - 신규API]: ${log}`,
            data: response.data,
          });
          message.success(t('transferSuccess') || '이체가 완료되었습니다');

          // 양쪽 지갑 모두 갱신
          mutateFrom();
          mutateTo();
          mutateTradingWallets();
        } else {
          mutateUserLog({
            log: `[지갑이동 실패 - 신규API]: ${log} / ${response.message}`,
            data: response,
          });
          message.error(response.message || '이체에 실패했습니다');
        }
      } else {
        // ===== 기존 Legacy API 사용 (현물/선물/글로벌/리워드) =====
        const data = {
          transfer_from: transferFrom,
          transfer_to: transferTo,
          coin_type: type,
          amount: amount,
        };

        mutateUserLog({
          log: `[지갑이동 요청]: ${log}`,
          data,
        });

        const res = await API.post(TRANSFER, token + '', data);

        if (res.result.result == true) {
          mutateUserLog({
            log: `[지갑이동 성공]: ${log}`,
            data,
          });
          message.success(t('transferSuccess') + '');
          mutateFrom();
          mutateTo();
        } else {
          // 특정 에러 코드 처리
          const errorCode = res?.result?.message;
          mutateUserLog({
            log: `[지갑이동 실패]: ${log} / ${errorCode}`,
            data,
          });
          if (errorCode === 'ERROR_NOBALANCE_EXCLUDE_EVENT') {
            message.error(
              t('errorNoBalanceExcludeEvent') ||
                '이벤트로 획득한 금액은 현물지갑으로 전송할 수 없습니다.'
            );
          } else {
            message.error(t(errorCode) + '');
          }
        }
      }
    } catch (err: any) {
      mutateUserLog({
        log: `[지갑이동 에러]: ${log} / ${err?.message || 'Unknown error'}`,
        data: err,
      });
      console.error('Transfer error:', err);
      message.error(err?.message || '이체 중 오류가 발생했습니다');
    } finally {
      setIsTransferring(false); // 전송 완료
    }
  };
  return (
    <View
      style={{
        width: '100%',
        justifyContent: 'flex-start',
      }}
    >
      <CButton
        title={t('confirm')}
        onClick={onTransfer}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: 12,
          background: COLORS.primary,
          color: COLORS.white,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 'bold',
        }}
      />
      {/* {transferFrom === 1 ? (
        <Text size={10} style={{ color: COLORS.lightRed, marginTop: 15 }}>
          {t("transferRedAlert")}
        </Text>
      ) : null} */}

      {/* <CButton
				title={t('toLoan')}
				style={{ color: COLORS.primary, marginTop: 15 }}
				icon={<IconChevronRight size={14} />}
			/> */}
    </View>
  );
};

export default TransferFooter;
